import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  DEFAULT_PIECE_BLACK_COLOR,
  DEFAULT_PIECE_MATERIAL,
  DEFAULT_PIECE_WHITE_COLOR,
  type PieceMaterialId,
} from '@chessmate/board-3d';
import { ASSISTANCE_PRESETS, type AssistancePresetId } from './assistance-presets.js';
import { DEFAULT_PIECE_SIZE, type PieceSizeId } from '@chessmate/board-3d';

/** Material family for the table that sits under the board. */
export type TableMaterialId = 'wood' | 'stone';
/** Tone axis for the table — independent of material. */
export type TableToneId = 'light' | 'dark';

/** Chain length for the multi-ply look-ahead hint. 1 = today's behaviour
 *  (single opponent reply). 2 / 3 extend the chain with the engine's
 *  predicted player counter-reply + opponent's next reply. The chain is
 *  capped at this value — a 2-ply PV at depth 3 still renders 2 rings. */
export type LookaheadDepth = 1 | 2 | 3;

export interface ThemeSettings {
  readonly pieceThemeId: string;
  readonly boardThemeId: string;
  readonly tableMaterialId: TableMaterialId;
  readonly tableToneId: TableToneId;
  readonly pieceMaterialId: PieceMaterialId;
  readonly pieceSizeId: PieceSizeId;
  readonly pieceWhiteColor: string;
  readonly pieceBlackColor: string;
  /** Phase 4 teaching-aid toggles. */
  readonly showCoords: boolean;
  readonly showMinimap: boolean;
  /** Ring the pieces the player can move this turn. */
  readonly highlightMovable: boolean;
  /** Show the path a selected piece travels, with stronger end squares. */
  readonly showMovePaths: boolean;
  /** Warn on destinations that are attacked and undefended (a piece lost). */
  readonly warnUnsafeMoves: boolean;
  readonly lookaheadEnabled: boolean;
  /** Multi-ply look-ahead depth; only consulted when `lookaheadEnabled`. */
  readonly lookaheadDepth: LookaheadDepth;
}

interface ThemeStore extends ThemeSettings {
  setPieceTheme(id: string): void;
  setBoardTheme(id: string): void;
  setTableMaterial(id: TableMaterialId): void;
  setTableTone(id: TableToneId): void;
  setPieceMaterial(id: PieceMaterialId): void;
  setPieceSize(id: PieceSizeId): void;
  setPieceWhiteColor(hex: string): void;
  setPieceBlackColor(hex: string): void;
  setShowCoords(on: boolean): void;
  setShowMinimap(on: boolean): void;
  setHighlightMovable(on: boolean): void;
  setShowMovePaths(on: boolean): void;
  setWarnUnsafeMoves(on: boolean): void;
  applyAssistancePreset(id: AssistancePresetId): void;
  setLookaheadEnabled(on: boolean): void;
  setLookaheadDepth(depth: LookaheadDepth): void;
}

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set) => ({
      pieceThemeId: 'classic',
      boardThemeId: 'classic',
      tableMaterialId: 'wood',
      tableToneId: 'light',
      pieceMaterialId: DEFAULT_PIECE_MATERIAL.id,
      pieceSizeId: DEFAULT_PIECE_SIZE.id,
      pieceWhiteColor: DEFAULT_PIECE_WHITE_COLOR,
      pieceBlackColor: DEFAULT_PIECE_BLACK_COLOR,
      showCoords: false,
      showMinimap: false,
      highlightMovable: false,
      showMovePaths: false,
      warnUnsafeMoves: false,
      lookaheadEnabled: true,
      lookaheadDepth: 1,
      setPieceTheme(id) {
        set({ pieceThemeId: id });
      },
      setBoardTheme(id) {
        set({ boardThemeId: id });
      },
      setTableMaterial(id) {
        set({ tableMaterialId: id });
      },
      setTableTone(id) {
        set({ tableToneId: id });
      },
      setPieceMaterial(id) {
        set({ pieceMaterialId: id });
      },
      setPieceSize(id) {
        set({ pieceSizeId: id });
      },
      setPieceWhiteColor(hex) {
        set({ pieceWhiteColor: hex });
      },
      setPieceBlackColor(hex) {
        set({ pieceBlackColor: hex });
      },
      setShowCoords(on) {
        set({ showCoords: on });
      },
      setShowMinimap(on) {
        set({ showMinimap: on });
      },
      setHighlightMovable(on) {
        set({ highlightMovable: on });
      },
      setShowMovePaths(on) {
        set({ showMovePaths: on });
      },
      setWarnUnsafeMoves(on) {
        set({ warnUnsafeMoves: on });
      },
      applyAssistancePreset(id) {
        set({ ...ASSISTANCE_PRESETS[id] });
      },
      setLookaheadEnabled(on) {
        set({ lookaheadEnabled: on });
      },
      setLookaheadDepth(depth) {
        set({ lookaheadDepth: depth });
      },
    }),
    {
      name: 's4k:themes',
      // Migration shim: older persisted blobs only carry the v1 fields; merge
      // in current defaults so reads of the new fields stay typed and don't
      // render `undefined` into the picker.
      merge: (persisted, current) => ({ ...current, ...(persisted as Partial<ThemeSettings>) }),
    },
  ),
);
